import React, { useState, useEffect } from "react";
import seedrandom from "seedrandom";
import { ProgressSpinner } from 'primereact/progressspinner';

import './CrosswordPage.css';
import words from "../data/words.json";
import { CrosswordGenerator, selectRandomEntries } from "../utils/CrosswordGenerator";

interface GridCell {
    value: string;
    clueIndices: number[];
    isVisible: boolean;
}

const MAX_WORDS = 11; // Maximum number of words to select for the crossword

const CrosswordPage: React.FC = () => {
    const [crosswordSolution, setCrosswordSolution] = useState<any>(null);
    const [grid, setGrid] = useState<GridCell[][]>([]);

    useEffect(() => {
        // console.log("CrosswordPage useEffect triggered");
        const today = new Date();
        const dailySeed =
            today.getFullYear().toString() +
            (today.getMonth() + 1).toString().padStart(2, "0") +
            today.getDate().toString().padStart(2, "0");

        setTimeout(() => {
            const rng = seedrandom(dailySeed);
            const crosswordGen = new CrosswordGenerator();
            const selectedEntries = selectRandomEntries(rng, words, MAX_WORDS);

            selectedEntries.forEach((word: any) => {
                // console.log(`Selected word: ${word.answer}, clue selected: ${Math.floor(rng() * word.clues.length)}`);
                word.answer = word.answer.toLowerCase();
                word.clue = word.clues.length > 0 ? word.clues[Math.floor(rng() * word.clues.length)] : "No clue.";
            });

            const solution = crosswordGen.generate(selectedEntries);
            setCrosswordSolution(solution);

            const initializedGrid: GridCell[][] = Array.from({ length: solution.gridYLength }, () =>
                Array.from({ length: solution.gridXLength }, () => ({
                    value: "",
                    clueIndices: [],
                    isVisible: false,
                }))
            );

            solution.gridSolutions.forEach((placedWord: any, idx: number) => {
                const length = placedWord.answer.length;
                for (let i = 0; i < length; i++) {
                    const x = placedWord.x + (placedWord.direction === "right" ? i : 0);
                    const y = placedWord.y + (placedWord.direction === "down" ? i : 0);
                    initializedGrid[y][x].isVisible = true;
                }
                initializedGrid[placedWord.y][placedWord.x].clueIndices.push(idx + 1);
            });

            setGrid(initializedGrid);
        }, 0); // defers execution to after paint
    }, []);

    if (!crosswordSolution) {
        return (
            <div className="loading-container">
                {/* <ProgressSpinner /> */}
                <ProgressSpinner style={{width: '50px', height: '50px'}} strokeWidth="8" fill="var(--surface-ground)" animationDuration=".5s" />
                <p>Loading...</p>
            </div>
        );
    }

    const handleInputChange = (value: string, x: number, y: number) => {
        const newGrid = [...grid];
        newGrid[y][x].value = value.toUpperCase();
        setGrid(newGrid);
    };

    const checkAnswers = () => {
        const nCorrectWords = crosswordSolution.gridSolutions.filter((solution: any) => {
            const { x, y, direction, answer } = solution;
            return [...answer.toLowerCase()].every((char: string, index: number) => {
                const currentX = direction === "right" ? x + index : x;
                const currentY = direction === "down" ? y + index : y;
                return grid[currentY][currentX].value.toLowerCase() === char;
            });
        }).length;

        const totalAnswers = crosswordSolution.gridSolutions.length;

        alert(
            nCorrectWords === totalAnswers
                ? "Congratulations! All answers are correct."
                : `You have ${nCorrectWords} out of ${totalAnswers} correct answers.`
        );
    };

    return (
        <div className="crossword-page">
            <div className="crossword-container">
                <h1>Daily Crossword</h1>

                <div className="crossword-grid">
                    {grid.map((row, y) => (
                        <div key={y} className="crossword-row">
                            {row.map((cell, x) => (
                                <div key={`${y}-${x}`} className="crossword-cell-wrapper">
                                    <input
                                        type="text"
                                        maxLength={1}
                                        value={cell.value}
                                        onChange={(e) => handleInputChange(e.target.value, x, y)}
                                        className={`crossword-cell ${cell.isVisible ? "" : "invisible"}`}
                                    />
                                    {cell.clueIndices.length > 0 && (
                                        <span className="clue-index">{cell.clueIndices.join(",")}</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>

                <div className="crossword-clues">
                    {crosswordSolution.gridSolutions.map((solution: any, index: number) => (
                        <div key={index} className="crossword-clue">
                            <strong>{index + 1} {solution.direction === "right" ? "→" : "↓"}</strong> {solution.clue}
                        </div>
                    ))}
                </div>

                <button onClick={checkAnswers} className="button-crossword">Check Answers</button>
            </div>
        </div>
    );
};

export default CrosswordPage;